import cx from 'classnames';
import { useRouter } from 'next/router';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import React, { useMemo, useState, Fragment } from 'react';

import useLinkProps, {
  originalRoutes,
} from '../../../../../hooks/use-link-props';
import { escapeRegExp } from '../../../../../utils/escapeRegExp';
import Breadcrumbs from '../../../../modules/v2/breadcrumbs';
import Trustpilot from '../../../../modules/trustpilot';
import ValueProposition from '../../../../modules/value-proposition';
import SubCategoriesSchema from './seo';

import Item from './item';
import classes from './subcategory.module.css';
import BPImage from '../../../../ui/image';
import useIsMobile from '../../../../../hooks/use-is-mobile';
import FlowTitleSEO from '../../../../ui/v2/flow-title';
import SearchInput from '../../vehicle/search-input';
import PageTitleSEO from '../../../../ui/v2/typography/page-title';
import PartPreview from '../../vehicle/part-preview';
import BrandHistory from '../../../../modules/v2/brand-history';
import LastEntries from '../../vehicle/last-entries';
import LastVehicles from '../../vehicle/last-vehicles';
import { getBrandHistory } from '../../../../../services/brand-history';
import SEOContent from './seo-content-section';

const SubCategory = ({
  groups,
  carInfo,
  lastEntries,
  vehicles,
  vehicle,
  stock,
  category,
  translatedSlugs,
}) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const { locale = 'en' } = useRouter();
  const getLink = useLinkProps();
  const [search, setSearch] = useState<string>('');
  const brandHistory = getBrandHistory({
    locale: locale,
    slug: carInfo?.brand?.slug,
  });

  const { selectedCategory, stockFromselectedCategory } = useMemo(() => {
    const selectedCategory = groups?.find(
      (g: TCategory) => g.slug === category
    );

    let stockFromselectedCategory: number = 0;
    Object.values(selectedCategory.parts).forEach((part) => {
      part.forEach((element) => {
        stockFromselectedCategory += element.total_products;
      });
    });

    return { selectedCategory, stockFromselectedCategory };
  }, [groups, category]);

  const filteredParts = useMemo(() => {
    if (search) {
      const regularExpression = new RegExp(escapeRegExp(search), 'i');
      return Object.keys(selectedCategory.parts).reduce((final, key) => {
        const filtered = selectedCategory.parts[key].filter((part) => {
          return part.name.match(regularExpression);
        });

        if (filtered.length > 0) {
          final[key] = filtered;
        }

        return final;
      }, {});
    }

    return selectedCategory.parts;
  }, [search, selectedCategory]);

  const regex = useMemo(() => {
    return new RegExp(escapeRegExp(`${search}`), 'ig');
  }, [search]);

  const breadcrumbItems: Array<{
    href: LinkProps;
    text: string;
  }> = [];

  if (carInfo) {
    breadcrumbItems.push({
      ...getLink('brands'),
      text: t('common:brand.other'),
    });

    breadcrumbItems.push({
      ...getLink('models', { brand: carInfo.brand.slug }),
      text: `${carInfo.brand.name} ${
        !carInfo.version ? carInfo.model.name : ''
      }`,
    });

    if (carInfo.version) {
      breadcrumbItems.push({
        ...getLink('versions', { model: carInfo.model.slug }),
        text: `${carInfo.model.name} ${carInfo.version.name}`,
      });
    }
  }

  breadcrumbItems.push({
    ...(carInfo
      ? getLink('categories-with-vehicle', { category: vehicle })
      : getLink('categories')),
    text: t('common:partscatalog'),
  });

  return (
    <main>
      <SubCategoriesSchema
        info={selectedCategory}
        breadcrumbs={breadcrumbItems}
        translatedSlugs={translatedSlugs}
        count={stockFromselectedCategory}
      />
      <Breadcrumbs
        items={breadcrumbItems}
        currentPage={selectedCategory.name}
      />
      <div className={cx()}>
        <FlowTitleSEO
          isTitleContainsImage={!isMobile && carInfo}
          imageContainer={
            !isMobile &&
            carInfo && (
              <div
                className={cx(
                  'relative',
                  'aspect-video',
                  'shrink-0',
                  'flex basis-5/12',
                  'w-full bg-neutral-5'
                )}
              >
                <BPImage
                  src={
                    carInfo?.model?.image ||
                    `${process.env.PUBLIC_PREFIX}/img/car_placeholder.jpeg`
                  }
                  layout="fill"
                  objectFit="contain"
                  objectPosition="center"
                  fallbackSrc={`${process.env.PUBLIC_PREFIX}/img/car_placeholder.jpeg`}
                  className={cx('bg-cover mix-blend-multiply')}
                  alt={t('common:images_alt.vehicle_alt', {
                    vehicle: `${carInfo?.brand?.name} ${carInfo?.model?.name}`,
                  })}
                />
              </div>
            )
          }
          cols={[
            <PageTitleSEO
              key={'title'}
              title={t('common:categories.parts.other', {
                part: selectedCategory?.slug
                  ? t(
                      `parts-and-categories-plural:categories.${selectedCategory?.slug}.plural`
                    )
                  : selectedCategory.name,
                count: stockFromselectedCategory.toLocaleString(locale),
                usedWord:
                  selectedCategory?.gender && selectedCategory?.gender === 'F'
                    ? t('grammar:words.used.feminine')
                    : t('grammar:words.used.masculine'),
              })}
              textAlign={carInfo && !isMobile ? 'left' : 'center'}
              categoryAndSubcategory={
                selectedCategory.name && (
                  <PartPreview groupName={selectedCategory.name} />
                )
              }
            />,
            carInfo && isMobile && (
              <div
                className={cx(
                  'relative',
                  'aspect-video',
                  'shrink-0',
                  'flex basis-5/12',
                  'w-full bg-neutral-5'
                )}
              >
                <BPImage
                  src={
                    carInfo?.model?.image ||
                    `${process.env.PUBLIC_PREFIX}/img/car_placeholder.jpeg`
                  }
                  layout="fill"
                  objectFit="contain"
                  objectPosition="center"
                  fallbackSrc={`${process.env.PUBLIC_PREFIX}/img/car_placeholder.jpeg`}
                  className={cx('bg-cover mix-blend-multiply')}
                  alt={t('common:images_alt.vehicle_alt', {
                    vehicle: `${carInfo?.brand.name} ${carInfo?.model?.name}`,
                  })}
                />
              </div>
            ),
            <h2
              key="stock"
              className={cx(
                'max-w-[780px] text-base lg:text-xl',
                !isMobile && carInfo ? 'max-lg:text-center' : 'text-center'
              )}
            >
              <Trans
                i18nKey={
                  carInfo
                    ? 'common:categories.parts.selectWithCar'
                    : 'common:categories.parts.selectNoCar'
                }
                components={{ strong: <strong /> }}
                values={{
                  count: stockFromselectedCategory.toLocaleString(locale),
                  part: selectedCategory.name,
                  usedWord:
                    selectedCategory?.gender && selectedCategory?.gender === 'F'
                      ? t('grammar:words.used.feminine')
                      : t('grammar:words.used.masculine'),
                  yoursWord:
                    selectedCategory?.gender && selectedCategory?.gender === 'F'
                      ? t('grammar:pronnouns.yoursPlural.feminine')
                      : t('grammar:pronnouns.yoursPlural.masculine'),
                }}
              />
            </h2>,
          ]}
        />
      </div>
      <div
        className={cx(
          'container',
          'mx-auto',
          'lg:mb-9',
          'lg:mt-6',
          'lg:px-4',
          'max-lg:pt-6'
        )}
      >
        <div
          className={cx(
            'flex',
            'w-full mx-auto max-lg:pt-0 lg:p-0 lg:my-12 sticky top-0 bg-white z-10'
          )}
        >
          <SearchInput
            search={search}
            placeholder={t('common:filterByPart')}
            setSearch={setSearch}
            isFullWith={true}
          />
        </div>

        <ul className={cx('lg:grid', 'pb-5', [classes.list])}>
          {Object.entries(filteredParts).map(([group_id, g], index) => (
            <div
              key={group_id}
              className={cx(
                'lg:grid',
                'lg:grid-cols-4',
                index < Object.entries(filteredParts).length - 1 ? 'pb-4' : ''
              )}
            >
              <Item
                category={category}
                group_id={group_id}
                group={g}
                regex={regex}
                vehicle={vehicle}
                key={group_id}
              />
            </div>
          ))}
        </ul>
      </div>
      {lastEntries && lastEntries.length > 0 && (
        <LastEntries products={lastEntries} vehicle={carInfo || {}} />
      )}
      <ValueProposition
        infoToDisplayInTitle={`${carInfo?.brand.name || ''} ${
          carInfo?.model?.name || ''
        } ${carInfo?.version?.name || ''}`}
      />
      {vehicles && vehicles.length > 0 && (
        <LastVehicles products={vehicles} vehicle={carInfo || {}} />
      )}
      <section className="w-full bg-blue-10 py-12 lg:py-16 text-justify ">
        {brandHistory && carInfo && (
          <BrandHistory
            imageURL={carInfo?.brand?.logo_url}
            {...brandHistory}
            brand={carInfo?.brand?.name}
            vehicle={carInfo}
            stock={stock && stock[1]?.stock.toLocaleString(locale)}
          />
        )}
        <SEOContent
          withCarInfo={!!carInfo}
          stock={stock[0]?.stock.toLocaleString(locale)}
          vehicle={carInfo}
          category={selectedCategory.name}
        ></SEOContent>
      </section>
      <div className="bg-white">
        <Trustpilot className={'py-8 lg:py-6'} backgroundColor="bg-white" />
      </div>
    </main>
  );
};

export default SubCategory;
